// TB-113: slot-level calendar model. Every slot keeps its real length and one
// state, so week, day, month and agenda views draw from the same data.
// Dates are "YYYY-MM-DD", times "HH:mm" wall clock (Bahrain), as the API returns.
import moment from 'moment'
import { clinicNow } from '@/utils/locale'
import { ISO, toMin, fromMin, isCancelled, typeKey, apptDate, apptTime, patientName, awaitingPayment, AWAITING_PAYMENT } from '@/utils/schedule'

export const DEFAULT_WINDOW = { from: 8 * 60, to: 17 * 60 }
export const STATES = ['free', 'booked', 'completed', 'blocked', 'past']
export const STATE_LABEL = {
  free: 'Free',
  booked: 'Booked',
  completed: 'Completed',
  blocked: 'Blocked',
  past: 'Past',
  cancelled: 'Cancelled'
}
// TB-121: colour means status only. One table for the legend popover (web) and
// the bottom sheet (app): swatch key, name, what it means, what you can do.
export const LEGEND = [
  { key: 'free', name: 'Free', means: 'Open slot, nobody booked', actions: 'Book, Block' },
  { key: 'booked', name: 'Booked', means: 'Booked, including video visits waiting for payment', actions: 'Start, Complete, Reschedule, Cancel' },
  { key: 'completed', name: 'Completed', means: 'Visit done', actions: 'View, Invoice' },
  { key: 'blocked', name: 'Blocked', means: 'You blocked it', actions: 'Unblock' },
  { key: 'notopen', name: 'Not opened', means: 'Inside working hours, no hours opened', actions: 'Add hours' },
  { key: 'outside', name: 'Outside hours', means: 'Outside working hours', actions: 'Nothing' },
  { key: 'pastzone', name: 'Past', means: 'In the past', actions: 'Nothing' },
  { key: 'cancelled', name: 'Cancelled', means: 'Cancelled (hidden by default)', actions: 'View' }
]
const DONE = ['completed', 'awaiting-invoice']

// Slot state name, with "Awaiting payment" for an unpaid video booking
export function slotStateLabel (s) {
  return s && s.appt && awaitingPayment(s.appt) ? AWAITING_PAYMENT.short : STATE_LABEL[s && s.state]
}

export function initials (name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  const first = parts[0][0] || ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return (first + last).toUpperCase()
}

function emptyDay (date) {
  return {
    date,
    slots: [],
    cancelled: [],
    blocks: [],
    blocked: false,
    blockReason: '',
    counts: { free: 0, booked: 0, completed: 0, blocked: 0, past: 0, cancelled: 0 }
  }
}

// Whole-day blocks have no times; a timed block covers [start, end).
export function blockCovers (block, start, end) {
  if (!block) return false
  if (!block.start || !block.end) return true
  return toMin(start) < toMin(block.end) && toMin(end) > toMin(block.start)
}

export function apptState (appt) {
  if (!appt) return null
  if (DONE.includes(appt.status)) return 'completed'
  return 'booked'
}

function isPastSlot (date, start, now) {
  const nowDate = now.format(ISO)
  if (date < nowDate) return true
  return date === nowDate && toMin(start) <= now.hours() * 60 + now.minutes()
}

function finish (days) {
  Object.values(days).forEach((d) => {
    d.slots.sort((a, b) => toMin(a.start) - toMin(b.start))
    d.cancelled.sort((a, b) => toMin(a.start) - toMin(b.start))
    d.slots.forEach((s) => { d.counts[s.state] = (d.counts[s.state] || 0) + 1 })
    d.counts.cancelled = d.cancelled.length
    if (!d.blocked) d.blocked = d.blocks.some((b) => !b.start || !b.end)
    const whole = d.blocks.find((b) => !b.start || !b.end)
    d.blockReason = (whole && whole.reason) || (d.blocks[0] && d.blocks[0].reason) || ''
  })
  return days
}

/**
 * Slot model from the endpoints that exist today:
 * schedules (GET schedule?period, slots included), appts (buildDays-shaped
 * appointments, every status) and blocks ([{ id, date, start, end, reason }]).
 */
export function slotDaysFromLegacy (schedules, rawAppts, blocks = [], typeTitles = {}, now = clinicNow()) {
  const days = {}
  const day = (d) => (days[d] = days[d] || emptyDay(d))
  const bySlot = {}
  const cancelled = []
  ;(rawAppts || []).forEach((a) => {
    if (!a || !a.startTime) return
    const title = typeTitles[a.consultationTypeId] || (a.consultationType && a.consultationType.title) || ''
    const appt = {
      id: a.id,
      date: apptDate(a.startTime),
      start: apptTime(a.startTime),
      end: apptTime(a.endTime),
      status: a.status,
      confirmed: !!a.confirmedAt,
      type: typeKey(a.consultationType || a.consultationTypeId || title),
      typeTitle: title,
      name: patientName(a),
      raw: a
    }
    if (isCancelled(a.status)) cancelled.push(appt)
    else if (a.slotId) bySlot[a.slotId] = appt
    else bySlot[`${appt.date} ${appt.start}`] = appt
  })
  ;(blocks || []).forEach((b) => { if (b && b.date) day(b.date).blocks.push(b) })

  ;(schedules || []).forEach((s) => {
    if (!s || !s.consultationDate) return
    const date = s.consultationDate
    const d = day(date)
    const title = (s.consultationType && s.consultationType.title) || ''
    const type = typeKey(s.consultationType || title)
    ;(s.slots || []).filter((x) => !x.deletedAt).forEach((x) => {
      const start = String(x.startTime || '').slice(0, 5)
      const end = String(x.endTime || '').slice(0, 5)
      if (toMin(start) === null) return
      const found = bySlot[x.id] || bySlot[`${date} ${fromMin(toMin(start))}`] || null
      // The visit type is the appointment's consultation type; the slot's when it is unknown.
      const appt = found && !found.typeTitle ? { ...found, type, typeTitle: title } : found
      let state
      const blockedBy = d.blocks.find((b) => blockCovers(b, start, end))
      if (appt) state = apptState(appt)
      else if (blockedBy || x.blocked === true) state = 'blocked'
      else if (x.status === false) state = 'booked'
      else state = isPastSlot(date, start, now) ? 'past' : 'free'
      d.slots.push({
        key: x.id,
        id: x.id,
        date,
        start: fromMin(toMin(start)),
        end: fromMin(toMin(end)),
        len: toMin(end) - toMin(start),
        scheduleId: s.id,
        type: appt ? appt.type : type,
        typeTitle: title,
        typeId: s.consultationType && s.consultationType.id,
        posId: s.purposes && s.purposes.id,
        purpose: (s.purposes && s.purposes.poc && s.purposes.poc.title) || '',
        color: (s.purposes && s.purposes.color) || '',
        fee: Number(s.perSlotAmount) || 0,
        state,
        appt,
        blockId: blockedBy ? blockedBy.id : null,
        blockReason: blockedBy ? blockedBy.reason || '' : ''
      })
    })
  })
  cancelled.forEach((a) => day(a.date).cancelled.push(a))
  return finish(days)
}

// Totals over a list of dates, for filter chips and the selection bar
export function totals (days, dates) {
  const out = { free: 0, booked: 0, completed: 0, blocked: 0, past: 0, cancelled: 0 }
  ;(dates || []).forEach((date) => {
    const d = days[date]
    if (!d) return
    Object.keys(out).forEach((k) => { out[k] += d.counts[k] || 0 })
  })
  return out
}

// Slots of a day inside a time range (selection)
export function slotsInRange (day, start, end) {
  if (!day) return []
  if (!start || !end) return day.slots
  return day.slots.filter((s) => toMin(s.start) < toMin(end) && toMin(s.end) > toMin(start))
}

// TB-168: one status model. The server folds "confirmed" into "booked" (TB-151), so
// booked is the only booked count; free + booked + completed + blocked + past = all slots
// of a day (cancelled slots are counted apart). Month bar, counts line, day header,
// side panel and filter chips all read these same counts.
export function pendingCount (day) {
  return ((day && day.slots) || []).filter((s) => s.state === 'booked' && awaitingPayment(s.appt)).length
}

export function dayBar (day) {
  if (!day || !day.counts) return []
  const c = day.counts
  const parts = [
    { key: 'free', n: c.free || 0 },
    { key: 'booked', n: c.booked || 0 },
    { key: 'completed', n: c.completed || 0 },
    { key: 'blocked', n: c.blocked || 0 },
    { key: 'past', n: c.past || 0 }
  ].filter((p) => p.n > 0)
  const total = parts.reduce((n, p) => n + p.n, 0)
  return parts.map((p) => ({ ...p, pct: total ? (p.n / total) * 100 : 0 }))
}

export function countsText (day) {
  if (!day || !day.counts) return ''
  const c = day.counts
  const parts = []
  if (c.free) parts.push(`${c.free} free`)
  if (c.booked) {
    const pend = pendingCount(day)
    parts.push(`${c.booked} booked${pend ? ` (${pend} awaiting payment)` : ''}`)
  }
  if (c.completed) parts.push(`${c.completed} done`)
  if (c.blocked) parts.push(`${c.blocked} blocked`)
  if (c.past) parts.push(`${c.past} passed`)
  return parts.join(' · ')
}

// Day header and month cell counts: [{ key, n, word }]; the words can be dropped, never the numbers
export function countParts (day) {
  if (!day || !day.counts) return []
  const c = day.counts
  const pend = pendingCount(day)
  return [
    { key: 'free', n: c.free || 0, word: 'free' },
    { key: 'booked', n: c.booked || 0, word: 'booked', pending: pend },
    { key: 'completed', n: c.completed || 0, word: 'done' },
    { key: 'blocked', n: c.blocked || 0, word: 'blocked' },
    { key: 'past', n: c.past || 0, word: 'passed' }
  ].filter((p) => p.n > 0)
}

// TB-168: purposes of a day for the month cell. Grouped by the purpose row (posId), not by
// name, so two purposes with the same name stay apart. used = booked + completed,
// total = every slot of that purpose that day except cancelled ones.
export function purposeSummary (day) {
  const m = new Map()
  ;((day && day.slots) || []).forEach((x) => {
    if (!x.purpose && !x.color) return
    const k = x.posId || `${x.purpose}|${x.color}`
    const cur = m.get(k) || { key: k, name: x.purpose || 'Consultation type', color: x.color || '', loc: x.locShort || '', used: 0, total: 0 }
    cur.total += 1
    if (x.state === 'booked' || x.state === 'completed') cur.used += 1
    m.set(k, cur)
  })
  const list = [...m.values()]
  list.forEach((p) => {
    const dupe = list.some((o) => o !== p && o.name === p.name) && !!p.loc
    p.tag = dupe ? p.loc : ''
    p.label = dupe ? `${p.name} (${p.loc})` : p.name
  })
  return list.sort((a, b) => b.used - a.used || b.total - a.total || a.label.localeCompare(b.label))
}

// "This week": fill rate, free slots left
export function weekStats (days, dates) {
  const t = totals(days, dates)
  const taken = t.booked + t.completed
  const offered = taken + t.free + t.past
  return {
    fill: offered ? Math.round((taken / offered) * 100) : 0,
    free: t.free,
    taken,
    offered
  }
}

// TB-121: grid range fits the day. Working hours, widened to cover slots, bookings and
// blocks, rounded to the hour. A working window of 16 hours or more says nothing about
// the day, so the grid then fits the data with an hour either side (08:00 to 17:00 at least).
export function fitHours (days, dates, windows = {}) {
  let dMin = null
  let dMax = null
  const take = (a, b) => {
    if (a === null || a === undefined) return
    dMin = dMin === null ? a : Math.min(dMin, a)
    dMax = dMax === null ? (b || a + 30) : Math.max(dMax, b || a + 30)
  }
  let wFrom = null
  let wTo = null
  ;(dates || []).forEach((date) => {
    const w = windows[date]
    if (w && w.to > w.from) {
      wFrom = wFrom === null ? w.from : Math.min(wFrom, w.from)
      wTo = wTo === null ? w.to : Math.max(wTo, w.to)
    }
    const d = days[date]
    if (!d) return
    d.slots.forEach((s) => take(toMin(s.start), toMin(s.end)))
    d.cancelled.forEach((a) => take(toMin(a.start), toMin(a.end)))
    d.blocks.forEach((b) => { if (b.start && b.end) take(toMin(b.start), toMin(b.end)) })
  })
  let from = DEFAULT_WINDOW.from
  let to = DEFAULT_WINDOW.to
  if (wFrom !== null && wTo - wFrom < 16 * 60) {
    from = dMin === null ? wFrom : Math.min(wFrom, dMin)
    to = dMax === null ? wTo : Math.max(wTo, dMax)
  } else {
    from = Math.min(DEFAULT_WINDOW.from, dMin === null ? DEFAULT_WINDOW.from : dMin - 60)
    to = Math.max(DEFAULT_WINDOW.to, dMax === null ? DEFAULT_WINDOW.to : dMax + 60)
    if (wFrom !== null) { from = Math.max(from, Math.floor(wFrom / 60) * 60); to = Math.min(to, Math.ceil(wTo / 60) * 60) }
  }
  return { from: Math.max(0, Math.floor(from / 60)), to: Math.min(24, Math.ceil(to / 60)) }
}

// First free slot at or after now, across the loaded days
export function nextFree (days, now = clinicNow()) {
  const keys = Object.keys(days).sort()
  for (const k of keys) {
    const s = days[k].slots.find((x) => x.state === 'free' && !isPastSlot(k, x.start, now))
    if (s) return s
  }
  return null
}

// Grid window for week/day: 08:00..17:00 widened to cover slots, blocks and bookings
export function slotHours (days, dates) {
  let min = 8 * 60
  let max = 17 * 60
  ;(dates || []).forEach((date) => {
    const d = days[date]
    if (!d) return
    d.slots.forEach((s) => { min = Math.min(min, toMin(s.start)); max = Math.max(max, toMin(s.end)) })
    d.cancelled.forEach((a) => { min = Math.min(min, toMin(a.start)); max = Math.max(max, toMin(a.end) || toMin(a.start) + 30) })
    d.blocks.forEach((b) => {
      if (b.start && b.end) { min = Math.min(min, toMin(b.start)); max = Math.max(max, toMin(b.end)) }
    })
  })
  return { from: Math.floor(min / 60), to: Math.min(24, Math.ceil(max / 60)) }
}

// Inclusive list of dates between two ISO dates (either order)
export function dateSpan (a, b) {
  const x = moment(a < b ? a : b, ISO)
  const y = moment(a < b ? b : a, ISO)
  const out = []
  for (const cur = x.clone(); !cur.isAfter(y); cur.add(1, 'day')) out.push(cur.format(ISO))
  return out
}

export function snap (min, step = 15) {
  return Math.round(min / step) * step
}

// Karim 2026-10-22: background zones of one day column, in minutes.
// past (before now, Bahrain wall time) > blocked (whole day) > outside (allowed
// working hours) > open (covered by slots) > notopen (allowed but no hours yet).

export function dayZones (day, date, { win = DEFAULT_WINDOW, from, to, today, nowMin }) {
  const slots = (day && day.slots) || []
  const blocked = !!(day && day.blocked)
  let pastEnd = from
  if (date < today) pastEnd = to
  else if (date === today && nowMin !== null && nowMin !== undefined) pastEnd = Math.max(from, Math.min(to, nowMin))
  const cuts = new Set([from, to, pastEnd, win.from, win.to])
  const spans = slots.map((s) => [toMin(s.start), toMin(s.end)])
  spans.forEach(([a, b]) => { cuts.add(a); cuts.add(b) })
  const pts = [...cuts].filter((m) => m >= from && m <= to).sort((a, b) => a - b)
  const out = []
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]
    const b = pts[i + 1]
    const mid = (a + b) / 2
    let kind
    if (mid < pastEnd) kind = 'past'
    else if (blocked) kind = 'blocked'
    else if (mid < win.from || mid >= win.to) kind = 'outside'
    else if (spans.some(([x, y]) => x <= mid && y > mid)) kind = 'open'
    else kind = 'notopen'
    const last = out[out.length - 1]
    if (last && last.kind === kind && last.end === a) last.end = b
    else out.push({ kind, start: a, end: b })
  }
  return out
}

export function zoneAt (zones, min) {
  const z = (zones || []).find((x) => min >= x.start && min < x.end)
  return z ? z.kind : null
}

// Minutes of [a, b) not covered by any slot of the day
function uncovered (day, a, b) {
  if (b <= a) return 0
  const spans = ((day && day.slots) || []).map((s) => [Math.max(a, toMin(s.start)), Math.min(b, toMin(s.end))]).filter(([x, y]) => y > x).sort((p, q) => p[0] - q[0])
  let covered = 0
  let cur = a
  spans.forEach(([x, y]) => {
    if (y <= cur) return
    covered += y - Math.max(x, cur)
    cur = y
  })
  return (b - a) - covered
}

/**
 * What a selection holds, so only valid actions are offered.
 * windowOf(date) -> { from, to } minutes. Past days and the elapsed part of today are left out.
 */
export function selectedSlots (days, sel) {
  if (!sel) return []
  const set = sel.ids ? new Set(sel.ids) : null
  const out = []
  ;(sel.dates || []).forEach((d) => {
    const day = days[d]
    if (!day) return
    ;(set ? day.slots.filter((s) => set.has(s.id)) : slotsInRange(day, sel.start, sel.end)).forEach((s) => out.push(s))
  })
  return out
}

export function selectionContent (days, sel, { today, nowMin, windowOf }) {
  const out = { free: 0, booked: 0, blocked: 0, completed: 0, passed: 0, blockedDays: 0, openDays: 0, notOpened: 0, freeSlots: [] }
  if (!sel) return out
  if (sel.ids) {
    selectedSlots(days, sel).forEach((s) => {
      if (s.state === 'free') { out.free++; out.freeSlots.push(s) } else if (s.state === 'booked' || s.state === 'confirmed') out.booked++
      else if (s.state === 'blocked') out.blocked++
      else if (s.state === 'completed') out.completed++
      else out.passed++
    })
    return out
  }
  ;(sel.dates || []).forEach((date) => {
    if (date < today) return
    const day = days[date] || null
    if (day && day.blocked) { out.blockedDays++; return }
    const w = windowOf(date) || DEFAULT_WINDOW
    if (w.to > w.from) out.openDays++
    slotsInRange(day, sel.start, sel.end).forEach((s) => {
      if (s.state === 'free') { out.free++; out.freeSlots.push(s) } else if (s.state === 'booked') out.booked++
      else if (s.state === 'blocked') out.blocked++
    })
    let a = Math.max(w.from, sel.start ? toMin(sel.start) : w.from)
    if (date === today && nowMin !== null && nowMin !== undefined) a = Math.max(a, nowMin)
    const b = Math.min(w.to, sel.end ? toMin(sel.end) : w.to)
    out.notOpened += uncovered(day, a, b)
  })
  return out
}
